// Invite Collaborator Modal Component
import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Users, Mail, CheckCircle, XCircle, UserPlus, Shield, Info } from 'lucide-react';

export function InviteCollaboratorModal({ isOpen, onClose, documentId, onInviteSent }) {
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('editor');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [seatInfo, setSeatInfo] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadSeatInfo();
    }
  }, [isOpen]);

  const loadSeatInfo = async () => {
    try {
      const info = await api.request('/collaborators/seats/info');
      setSeatInfo(info);
    } catch (err) {
      console.error('Failed to load seat info:', err);
    }
  };

  const handleInvite = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (email !== confirmEmail) {
      setError('Email addresses do not match');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.request(`/collaborators/invite`, {
        method: 'POST',
        body: JSON.stringify({
          document_id: documentId,
          invitee_email: email,
          invitee_name: name || null,
          role: role
        })
      });

      const displayName = name || email;
      setSuccess(
        response.existing_user
          ? `Invitation sent! ${displayName} is already a UPost user.`
          : `Invitation sent! ${displayName} will receive an email to join.`
      );
      setEmail('');
      setConfirmEmail('');
      setName('');
      
      if (onInviteSent) {
        onInviteSent(response);
      }

      setTimeout(() => {
        onClose();
        setSuccess('');
      }, 2000);

    } catch (err) {
      console.error('Invite error:', err);
      setError(err.message || err.detail || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden border border-slate-200 shadow-2xl rounded-xl" data-testid="invite-collaborator-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-5">
          <DialogHeader className="space-y-1">
            <DialogTitle className="flex items-center gap-2.5 text-white text-lg font-semibold">
              <div className="p-1.5 rounded-lg bg-white/10">
                <UserPlus className="h-5 w-5 text-white" />
              </div>
              Invite Collaborator
            </DialogTitle>
            <DialogDescription className="text-slate-300 text-sm leading-relaxed">
              Share this document with a teammate. They can edit or comment based on the role you assign.
            </DialogDescription>
          </DialogHeader>

          {/* Seat Info - integrated into header */}
          {seatInfo && (
            <div className={`mt-4 flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium ${
              seatInfo.available
                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20'
                : 'bg-amber-500/15 text-amber-300 border border-amber-500/20'
            }`}>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Seats: {seatInfo.used} / {seatInfo.limit} used</span>
              </div>
              {!seatInfo.available && (
                <a href="/pricing" className="text-xs underline underline-offset-2 hover:text-amber-200 transition-colors">
                  Upgrade plan
                </a>
              )}
            </div>
          )}
        </div>

        {/* Form Body */}
        <div className="px-6 py-5 space-y-5">
          {error && (
            <Alert variant="destructive" className="bg-red-50 border-red-300 text-red-800">
              <XCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700 font-medium">{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-emerald-50 border-emerald-300">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-emerald-800 font-medium">{success}</AlertDescription>
            </Alert>
          )}

          {/* Name */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-900" style={{color: '#111827'}}>Name</label>
            <Input
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              className="h-11 bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-slate-500 focus:ring-slate-500 transition-all"
              data-testid="invite-name-input"
            />
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-900" style={{color: '#111827'}}>Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="colleague@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                className="pl-10 h-11 bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-slate-500 focus:ring-slate-500 transition-all"
                disabled={loading}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                name={`invite-email-${Math.random()}`}
                id={`invite-email-${Math.random()}`}
                data-lpignore="true"
                data-form-type="other"
                data-testid="invite-email-input"
              />
            </div>
          </div>

          {/* Confirm Email */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-900" style={{color: '#111827'}}>Confirm Email Address</label>
            <div className="relative">
              <Shield className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Re-type email to confirm"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value.toLowerCase().trim())}
                onPaste={(e) => {
                  e.preventDefault();
                  setError('Please type the email address instead of pasting');
                  setTimeout(() => setError(''), 3000);
                }}
                onCopy={(e) => { e.preventDefault(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  setError('Please type the email address instead of dragging/dropping');
                  setTimeout(() => setError(''), 3000);
                }}
                className="pl-10 h-11 bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-slate-500 focus:ring-slate-500 transition-all"
                disabled={loading}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                name="confirm_email_verification"
                id="confirm_email_verification"
                data-lpignore="true"
                data-form-type="other"
                data-1p-ignore="true"
                aria-autocomplete="none"
                role="presentation"
                data-testid="invite-confirm-email-input"
              />
            </div>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Info className="h-3 w-3" />
              Type again to prevent typos — paste is disabled
            </p>
          </div>

          {/* Role */}
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-900" style={{color: '#111827'}}>Role</label>
            <Select value={role} onValueChange={setRole} disabled={loading}>
              <SelectTrigger className="h-11 bg-slate-50 border-slate-300 text-slate-900 focus:border-slate-500 focus:ring-slate-500" data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border border-slate-200 shadow-lg z-[9999]">
                <SelectItem value="editor" className="text-slate-900 focus:bg-slate-100 focus:text-slate-900 cursor-pointer py-2.5">Editor — Can edit and comment</SelectItem>
                <SelectItem value="viewer" className="text-slate-900 focus:bg-slate-100 focus:text-slate-900 cursor-pointer py-2.5">Viewer — Can view and comment</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Info callout */}
          <div className="flex items-start gap-3 p-3.5 rounded-lg bg-slate-100 border border-slate-200">
            <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-600 leading-relaxed">
              <strong className="text-slate-800">New users welcome.</strong> If they don't have an account, they'll get a free UPost account when they accept.
            </p>
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 h-11 border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 font-medium transition-all"
            disabled={loading}
            data-testid="invite-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleInvite}
            className="flex-1 h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold shadow-md hover:shadow-lg transition-all"
            disabled={loading}
            data-testid="invite-send-btn"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} />
                Sending...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Send Invitation
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
